import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import { Button, Paper, Stack, Typography } from '@mui/material';
import type { OpenConsultantDeadlinesAction } from '@prospero/shared/src/assistant/assistant-contract';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { TOUCH_TARGET_PX } from '../../config/touch-target';
import { buildDeadlineCalendarLink } from '../../pages/consultant/deadlines/deadline-calendar-link';
import { formatIsoDay } from '../../pages/consultant/deadlines/deadline-row-format';

import { DeadlineLinksViewAllButton } from './deadline-links-view-all-button';

const KEY = 'guida.assistant.consultantDeadlines';

/** The deadlines a reply cited, each one a jump to its highlighted week. */
export function ConsultantDeadlineLinksCard({ action }: { action: OpenConsultantDeadlinesAction }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const remainingCount = action.total - action.links.length;
  // A row with neither name, title nor type: the button still says what it opens.
  const untitledLabel = t(`${KEY}.untitledDeadline`, { defaultValue: 'Scadenza' });
  const title = t(`${KEY}.linksTitle`, { defaultValue: 'Nello scadenzario' });
  return (
    <Paper aria-label={title} role="region" sx={{ borderRadius: 2, p: 2 }} variant="outlined">
      <Stack spacing={0.75}>
        <Typography sx={{ fontWeight: 700 }} variant="body2">
          {title}
        </Typography>
        {action.links.map((link) => (
          <Button
            data-testid={`consultant-deadline-link-${link.deadlineId}`}
            fullWidth
            key={link.deadlineId}
            onClick={() => navigate(buildDeadlineCalendarLink(link))}
            startIcon={<EventOutlinedIcon />}
            sx={{
              justifyContent: 'flex-start',
              minHeight: TOUCH_TARGET_PX,
              textAlign: 'left',
              textTransform: 'none',
            }}
            variant="outlined"
          >
            {`${link.label.trim() || untitledLabel} · ${link.tenantName} · ${formatIsoDay(link.dueAt, i18n.language)}`}
          </Button>
        ))}
        {action.url ? (
          <DeadlineLinksViewAllButton
            shownCount={action.links.length}
            total={action.total}
            url={action.url}
          />
        ) : remainingCount > 0 ? (
          <Typography color="text.secondary" variant="caption">
            {t(`${KEY}.moreInTracker`, {
              count: remainingCount,
              defaultValue_one: "Un'altra nello scadenzario",
              defaultValue: 'Altre {{count}} nello scadenzario',
            })}
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  );
}
